import { Injectable, Optional } from '@angular/core';
import { Messaging } from '@angular/fire/messaging';
import { getToken, onMessage } from 'firebase/messaging';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { firebaseConfig } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class MessagingService {
  currentMessage = new BehaviorSubject<any>(null);

  constructor(
    @Optional() private messaging: Messaging | null,
    private http: HttpClient
  ) {
    // Only initialize if messaging is available and we're in a browser environment
    // Check if we're in a browser and messaging is properly initialized
    if (typeof window !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      this.messaging &&
      this.isMessagingAvailable()) {
      try {
        // Delay initialization to ensure everything is ready
        setTimeout(() => {
          try {
            this.listen();
          } catch (error) {
            console.warn('[FCM] Firebase messaging initialization failed:', error);
          }
        }, 100);
      } catch (error) {
        console.warn('[FCM] Firebase messaging not available:', error);
      }
    } else {
      console.warn('[FCM] Messaging skipped - not available in this environment');
    }
  }

  private isMessagingAvailable(): boolean {
    try {
      // Check if messaging object exists and we're in a browser environment
      if (!this.messaging || typeof this.messaging !== 'object') {
        return false;
      }

      // Check if we're in a browser environment with service worker support
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return false;
      }

      // Service worker support is optional, but messaging should still work
      return true;
    } catch {
      return false;
    }
  }

  requestPermission() {
    if (!this.messaging) {
      console.warn('[FCM] Firebase messaging is not available');
      return;
    }
    if (!firebaseConfig?.vapidKey) {
      console.error('[FCM] VAPID key is missing in firebaseConfig');
      return;
    }

    try {
      getToken(this.messaging, { vapidKey: firebaseConfig.vapidKey })
        .then((token) => {
          if (token) {
            this.saveToken(token).subscribe({
              error: (err) => console.error('[FCM] Failed to save token', err),
            });
          } else {
            console.warn('[FCM] getToken resolved with no token');
          }
        })
        .catch((err) => {
          console.error('[FCM] Unable to get permission to notify.', err);
        });
    } catch (error) {
      console.warn('[FCM] Error requesting notification permission:', error);
    }
  }

  saveToken(token: string) {
    return this.http.post(`${environment.apiUrl}/v1/FcmToken/${token}`, {});
  }

  listen() {
    if (!this.messaging || !this.isMessagingAvailable()) {
      // console.warn('[FCM] listen() - Firebase messaging is not available');
      return;
    }

    try {
      // Double-check messaging is valid before calling onMessage
      if (this.messaging && typeof onMessage === 'function') {
        onMessage(this.messaging, (payload) => {
          this.currentMessage.next(payload);
        });
      }
    } catch (error) {
      console.warn('[FCM] Error setting up message listener:', error);
    }
  }
}
